import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { AtSignIcon, GlobeIcon, GraduationCapIcon, HomeIcon, IdCardIcon, PencilIcon, PhoneIcon, PlusIcon, SaveIcon, Share2Icon } from 'lucide-react';
import { toast } from 'sonner';
import type { EducationEntry, Employee, ProfileDetails } from '../../types/hr';
import { age, formatDate } from '../../utils/format';
import { hrActions } from '../../utils/hrStore';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Field } from '../ui/Field';
import { TextField } from '../ui/TextField';
import { SensitiveField } from './SensitiveField';
interface PersonalTabProps {
  employee: Employee;
  details: ProfileDetails;
  editing: boolean;
  onEdit: () => void;
  onEditDone: () => void;
}
interface EmployeeDraft {
  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;
  gender: string;
  maritalStatus: string;
  shirtSize: string;
  workPhone: string;
  workExt: string;
  mobile: string;
  workEmail: string;
}
interface DetailsDraft {
  address: ProfileDetails['address'];
  homePhone: string;
  homeEmail: string;
  social: ProfileDetails['social'];
}
const EMPTY_EDU: Omit<EducationEntry, 'id'> = {
  institution: '',
  degree: '',
  major: '',
  gpa: '',
  start: '',
  end: ''
};
function toEmployeeDraft(e: Employee): EmployeeDraft {
  return {
    firstName: e.firstName,
    middleName: e.middleName ?? '',
    lastName: e.lastName,
    preferredName: e.preferredName ?? '',
    gender: e.gender,
    maritalStatus: e.maritalStatus,
    shirtSize: e.shirtSize,
    workPhone: e.workPhone,
    workExt: e.workExt,
    mobile: e.mobile,
    workEmail: e.workEmail
  };
}
function toDetailsDraft(d: ProfileDetails): DetailsDraft {
  return {
    address: {
      ...d.address
    },
    homePhone: d.homePhone,
    homeEmail: d.homeEmail,
    social: {
      ...d.social
    }
  };
}
export function PersonalTab({
  employee,
  details,
  editing,
  onEdit,
  onEditDone
}: PersonalTabProps) {
  const [emp, setEmp] = useState<EmployeeDraft>(() => toEmployeeDraft(employee));
  const [det, setDet] = useState<DetailsDraft>(() => toDetailsDraft(details));
  const [addingEdu, setAddingEdu] = useState(false);
  const [eduDraft, setEduDraft] = useState(EMPTY_EDU);
  useEffect(() => {
    if (editing) {
      setEmp(toEmployeeDraft(employee));
      setDet(toDetailsDraft(details));
    }
    // Reset drafts only when entering edit mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  const setE = (key: keyof EmployeeDraft) => (v: string) => setEmp(p => ({
    ...p,
    [key]: v
  }));
  const setA = (key: keyof DetailsDraft['address']) => (v: string) => setDet(p => ({
    ...p,
    address: {
      ...p.address,
      [key]: v
    }
  }));
  const setS = (key: keyof DetailsDraft['social']) => (v: string) => setDet(p => ({
    ...p,
    social: {
      ...p.social,
      [key]: v
    }
  }));
  const save = () => {
    if (!emp.firstName.trim() || !emp.lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }
    if (!emp.workEmail.includes('@')) {
      toast.error('Enter a valid work email');
      return;
    }
    hrActions.updateEmployee(employee.id, {
      ...emp,
      firstName: emp.firstName.trim(),
      lastName: emp.lastName.trim(),
      middleName: emp.middleName.trim() || undefined,
      preferredName: emp.preferredName.trim() || undefined
    });
    hrActions.saveDetails(employee.id, {
      ...details,
      ...det
    });
    toast.success('Profile saved', {
      description: `${emp.preferredName.trim() || emp.firstName} ${emp.lastName}'s details were updated.`
    });
    onEditDone();
  };
  const saveEducation = () => {
    if (!eduDraft.institution.trim()) {
      toast.error('Institution is required');
      return;
    }
    hrActions.saveDetails(employee.id, {
      ...details,
      education: [...details.education, {
        ...eduDraft,
        id: `ed-${Date.now()}`
      }]
    });
    setEduDraft(EMPTY_EDU);
    setAddingEdu(false);
    toast.success('Education added');
  };
  const view = (k: keyof EmployeeDraft): string => editing ? emp[k] : toEmployeeDraft(employee)[k];
  const address = editing ? det.address : details.address;
  const social = editing ? det.social : details.social;
  return <div className="space-y-6" data-id="element-283" data-mp="6f2oqel9693u">
      {editing && <div className="sticky top-[136px] z-[5] flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-navy px-5 py-3 text-white shadow-lift" data-id="element-284" data-mp="6e9db0bcmvfd">
          <p className="flex items-center gap-2 text-sm font-medium" data-id="element-285" data-mp="f7meepjkh3cj">
            <PencilIcon className="h-4 w-4 text-azure" data-id="element-286" data-mp="k6zbs482dstd" /> Editing personal details
          </p>
          <div className="flex gap-2" data-id="element-287" data-mp="p627gy25mmoo">
            <Button size="sm" variant="glass" onClick={onEditDone} data-id="element-288" data-mp="p74xl3rnkl3j">
              Cancel
            </Button>
            <Button size="sm" variant="primary" icon={SaveIcon} onClick={save} data-id="element-289" data-mp="u4wzmqm0hnlg">
              Save changes
            </Button>
          </div>
        </div>}

      <Card title="Basic Information" icon={IdCardIcon} action={!editing && <Button variant="ghost" size="sm" icon={PencilIcon} onClick={onEdit} data-id="element-291" data-mp="1d2qff5vz36ku">
              Edit
            </Button>} data-id="element-290" data-mp="2vw9u0lqcsd39">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-292" data-mp="149mvceqtnvh8">
          <Field label="Employee #" value={employee.employeeNumber} data-id="element-293" data-mp="1478zw4s8ch95" />
          <Field label="Status" value={employee.status} data-id="element-294" data-mp="zc3hvh1y7mqc" />
          <EditField label="Preferred Name" value={view('preferredName')} editing={editing} onChange={setE('preferredName')} data-id="element-295" data-mp="1w1b28lqtzpso" />
          <EditField label="First Name" value={view('firstName')} editing={editing} onChange={setE('firstName')} data-id="element-296" data-mp="1s0iujwxlxsyi" />
          <EditField label="Middle Name" value={view('middleName')} editing={editing} onChange={setE('middleName')} data-id="element-297" data-mp="1myjryp9oz02m" />
          <EditField label="Last Name" value={view('lastName')} editing={editing} onChange={setE('lastName')} data-id="element-298" data-mp="1i34gu532ji9w" />
          <SensitiveField label="Birth Date" masked={`**/**/${employee.birthDate.slice(0, 4)}`} revealed={formatDate(employee.birthDate, 'MM/dd/yyyy')} maskedDetail="Age hidden" revealedDetail={`Age ${age(employee.birthDate)}`} data-id="element-299" data-mp="6luvte4aki6h" />
          <SensitiveField label="SSN" masked={`***-**-${employee.ssn.slice(-4)}`} revealed={employee.ssn} data-id="element-300" data-mp="3v7q8duiewdiy" />
          <EditField label="Gender" value={view('gender')} editing={editing} onChange={setE('gender')} data-id="element-301" data-mp="416nfmiv376x" />
          <EditField label="Marital Status" value={view('maritalStatus')} editing={editing} onChange={setE('maritalStatus')} data-id="element-302" data-mp="3li6msh3be5ke" />
          <EditField label="Shirt Size" value={view('shirtSize')} editing={editing} onChange={setE('shirtSize')} data-id="element-303" data-mp="3q9ond41fe44u" />
        </dl>
      </Card>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2" data-id="element-304" data-mp="3cf5jsp9qwuky">
        <Card title="Address" icon={HomeIcon} data-id="element-305" data-mp="3gjn0ugoq0ts1">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2" data-id="element-306" data-mp="32j2gvh72krwy">
            <EditField label="Street 1" value={address.street1} editing={editing} onChange={setA('street1')} data-id="element-307" data-mp="32juawnl0qhl4" />
            <EditField label="Street 2" value={address.street2} editing={editing} onChange={setA('street2')} data-id="element-308" data-mp="37k8dvr7rj1ca" />
            <EditField label="City" value={address.city} editing={editing} onChange={setA('city')} data-id="element-309" data-mp="2ixad3x4nwcjn" />
            <EditField label="State" value={address.state} editing={editing} onChange={setA('state')} data-id="element-310" data-mp="2xujivvuppiuw" />
            <EditField label="ZIP" value={address.zip} editing={editing} onChange={setA('zip')} data-id="element-311" data-mp="2svggkj649qqm" />
            <EditField label="Country" value={address.country} editing={editing} onChange={setA('country')} data-id="element-312" data-mp="36v7xosrxjn8u" />
          </dl>
        </Card>

        <Card title="Contact" icon={PhoneIcon} data-id="element-313" data-mp="31v3j58al12l9">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2" data-id="element-314" data-mp="31uafig6x6t7c">
            {editing ? <div className="grid grid-cols-[1fr_80px] gap-2" data-id="element-315" data-mp="3bly3u886ju4l">
                <EditField label="Work Phone" value={emp.workPhone} editing onChange={setE('workPhone')} data-id="element-316" data-mp="3bmg89s4zxenl" />
                <EditField label="Ext." value={emp.workExt} editing onChange={setE('workExt')} data-id="element-317" data-mp="3plz2ri4vwrhf" />
              </div> : <Field label="Work Phone" value={<>
                    {employee.workPhone} <span className="text-muted" data-id="element-319" data-mp="10rzbmfcnxlq4">Ext. {employee.workExt}</span>
                  </>} data-id="element-318" data-mp="vtxwg5icy6jx" />}
            <EditField label="Mobile Phone" value={view('mobile')} editing={editing} onChange={setE('mobile')} data-id="element-320" data-mp="15pr4pcp1ndvw" />
            <EditField label="Home Phone" value={editing ? det.homePhone : details.homePhone} editing={editing} onChange={v => setDet(p => ({
            ...p,
            homePhone: v
          }))} data-id="element-321" data-mp="19r4bpq3vhn4e" />
            <EditField label="Work Email" value={view('workEmail')} editing={editing} onChange={setE('workEmail')} display={<span className="break-all text-azure" data-id="element-323" data-mp="qve9c1s12opz">{employee.workEmail}</span>} data-id="element-322" data-mp="i2suuxvfj46y" />
            <EditField label="Home Email" value={editing ? det.homeEmail : details.homeEmail} editing={editing} onChange={v => setDet(p => ({
            ...p,
            homeEmail: v
          }))} display={<span className="break-all" data-id="element-325" data-mp="3my2kfqh5gopg">{details.homeEmail}</span>} data-id="element-324" data-mp="3qh1gflzjaupm" />
          </dl>
        </Card>
      </div>

      <Card title="Social Links" icon={Share2Icon} data-id="element-326" data-mp="3dzgtp81iy3dl">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-327" data-mp="3dxxdfhfvbsbt">
          {([{
          key: 'linkedin',
          label: 'LinkedIn'
        }, {
          key: 'facebook',
          label: 'Facebook'
        }, {
          key: 'instagram',
          label: 'Instagram'
        }] as const).map(s => editing ? <EditField key={s.key} label={s.label} value={social[s.key]} editing onChange={setS(s.key)} data-id="element-328" data-mp="2z8rgk04i6e73" /> : <div key={s.key} className="flex items-center gap-3 rounded-xl bg-canvas/70 px-4 py-3 ring-1 ring-inset ring-slate-100" data-id="element-329" data-mp="2qa62ah62dlc4">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-navy shadow-sm" data-id="element-330" data-mp="2l9i1x4vy3s58">
                  {s.key === 'linkedin' ? <GlobeIcon className="h-4 w-4" data-id="element-331" data-mp="2ge2r6lt8icd1" /> : <AtSignIcon className="h-4 w-4" data-id="element-332" data-mp="xlj4k9e0n3oi" />}
                </span>
                <div className="min-w-0" data-id="element-333" data-mp="tkpizmo1q0ys">
                  <dt className="text-[11px] font-medium uppercase tracking-wider text-muted" data-id="element-334" data-mp="tjxuswws35ij">{s.label}</dt>
                  <dd className={`truncate text-sm ${social[s.key] ? 'text-azure' : 'text-slate-300'}`} data-id="element-335" data-mp="17bm64xz7dpyg">{social[s.key] || 'Not added'}</dd>
                </div>
              </div>)}
        </dl>
      </Card>

      <Card title="Education" icon={GraduationCapIcon} subtitle={`${details.education.length} ${details.education.length === 1 ? 'entry' : 'entries'}`} action={!addingEdu && <Button variant="ghost" size="sm" icon={PlusIcon} onClick={() => setAddingEdu(true)} data-id="element-337" data-mp="jmt4qlztapwg">
              Add Education
            </Button>} data-id="element-336" data-mp="ep9tluyeut8q">
        {details.education.length === 0 && !addingEdu && <p className="text-sm text-muted" data-id="element-338" data-mp="1v2hx5svu4h4n">No education added yet.</p>}
        <ul className="space-y-3" data-id="element-339" data-mp="3czbjgwnegfld">
          {details.education.map(ed => <li key={ed.id} className="grid grid-cols-2 gap-4 rounded-xl px-4 py-4 ring-1 ring-inset ring-slate-100 md:grid-cols-5" data-id="element-340" data-mp="3d1o79qfu9k6c">
              <div className="col-span-2" data-id="element-341" data-mp="33z6hx34w0bf4">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted" data-id="element-342" data-mp="340hn97dzdr8c">Institution</p>
                <p className="mt-1 text-sm font-medium text-navy" data-id="element-343" data-mp="38xzw6xs006gc">{ed.institution}</p>
              </div>
              <Field label="Degree" value={ed.degree} data-id="element-344" data-mp="k13c50bp172" />
              <Field label="Major" value={ed.major} data-id="element-345" data-mp="4igmtyx73rfq" />
              <div className="flex gap-6" data-id="element-346" data-mp="4lckrn91mg46">
                <Field label="GPA" value={ed.gpa} data-id="element-347" data-mp="3mwg58vdc291n" />
                <Field label="Start–End" value={ed.start || ed.end ? `${ed.start}–${ed.end}` : ''} data-id="element-348" data-mp="3mvoj0b5mlno1" />
              </div>
            </li>)}
        </ul>
        {addingEdu && <div className="mt-4 rounded-xl bg-canvas/70 p-4 ring-1 ring-inset ring-azure/20" data-id="element-349" data-mp="jepkiw1ftwq1">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-350" data-mp="o8tt1jqcqct8">
              <TextField label="Institution" value={eduDraft.institution} onChange={v => setEduDraft({
            ...eduDraft,
            institution: v
          })} placeholder="e.g. Example University" data-id="element-351" data-mp="y2zc8guj7gr4" />
              <TextField label="Degree" value={eduDraft.degree} onChange={v => setEduDraft({
            ...eduDraft,
            degree: v
          })} placeholder="Bachelor's" data-id="element-352" data-mp="o4ig75gqkby7" />
              <TextField label="Major" value={eduDraft.major} onChange={v => setEduDraft({
            ...eduDraft,
            major: v
          })} placeholder="Finance" data-id="element-353" data-mp="j8be5nrgnklj" />
              <TextField label="GPA" value={eduDraft.gpa} onChange={v => setEduDraft({
            ...eduDraft,
            gpa: v
          })} placeholder="3.5" data-id="element-354" data-mp="adn4zmirlirg" />
              <TextField label="Start year" value={eduDraft.start} onChange={v => setEduDraft({
            ...eduDraft,
            start: v
          })} placeholder="2010" data-id="element-355" data-mp="5fbtl7ctn074" />
              <TextField label="End year" value={eduDraft.end} onChange={v => setEduDraft({
            ...eduDraft,
            end: v
          })} placeholder="2014" data-id="element-356" data-mp="3rlsdhrnuypql" />
            </div>
            <div className="mt-4 flex justify-end gap-2" data-id="element-357" data-mp="3nl8fymzdhk3x">
              <Button size="sm" onClick={() => setAddingEdu(false)} data-id="element-358" data-mp="2335yrr8gpeyn">
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={saveEducation} data-id="element-359" data-mp="272yd6kkjefbx">
                Save education
              </Button>
            </div>
          </div>}
      </Card>
    </div>;
}
interface EditFieldProps {
  label: string;
  value: string;
  editing: boolean;
  onChange: (value: string) => void;
  display?: ReactNode;
}
function EditField({
  label,
  value,
  editing,
  onChange,
  display
}: EditFieldProps) {
  if (!editing) return <Field label={label} value={display ?? value} data-id="element-360" data-mp="1ob5gog69ebzg" />;
  const id = `edit-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return <div data-id="element-361" data-mp="1y3t9kzu4bvsw">
      <label htmlFor={id} className="text-[11px] font-medium uppercase tracking-wider text-muted" data-id="element-362" data-mp="1y4cvssqsx6cq">
        {label}
      </label>
      <input id={id} value={value} onChange={e => onChange(e.target.value)} className="mt-1 h-10 w-full rounded-lg border-0 bg-white px-3 text-sm text-navy ring-1 ring-inset ring-slate-200 focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-363" data-mp="10nnkm8fyorqo" />
    </div>;
}